import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useT, itemName, rarityLabel } from '@/i18n';
import { calcPlayerStats } from '@/domain/combat';
import { getEquippedStats, rarityRank } from '@/domain/equipment';
import { getItem } from '@/domain/items';
import { upgradedStats } from '@/domain/upgrade';
import { xpRequiredForLevel } from '@/domain/constants';
import { SET_NAMES } from '@/domain/itemSets';
import { totalAugmentValue } from '@/domain/augments';
import {
  ASPECTS,
  affinityMultiplier,
  countAffinityPieces,
  totalAspectValue,
  type AspectId,
} from '@/domain/aspects';
import { useGameStore } from '@/state/gameStore';
import { useMarketStore } from '@/state/marketStore';
import type { EquipmentInstance, EquipmentSlot, EquippedMap, ItemSetId, Rarity } from '@/domain/types';

import { ItemIcon } from '@/ui/components/ItemIcon';
import { UpgradeModal } from '@/ui/components/UpgradeModal';
import { ItemDetailModal } from '@/ui/components/ItemDetailModal';
import { AchievementsPanel } from '@/ui/components/AchievementsPanel';
import { DailyQuestPanel } from '@/ui/components/DailyQuestPanel';
import { AspectPanel } from '@/ui/components/AspectPanel';
import { SkillSlots } from '@/ui/components/SkillSlots';
import { SetBonusBar } from '@/ui/components/SetBonusBar';
import { CharacterPortrait3D } from '@/ui/components/CharacterPortrait3D';
import { CharacterPortraitPixel } from '@/ui/components/CharacterPortraitPixel';
import './CharacterScreen.css';

interface CharacterScreenProps {
  /** Optional — kept for backwards-compat with legacy nav. */
  onBack?: () => void;
}

const RARITY_COLOR: Record<Rarity, string> = {
  common: '#a4b8c8', uncommon: '#4affb0', rare: '#4aa7ff',
  epic: '#c64aff', legendary: '#ff9a2b', mythic: '#ff4af0',
};

const SLOT_GLYPH: Record<EquipmentSlot, string> = {
  weapon: '⚔', helmet: '⬡', armor: '◫', accessory: '◉', shield: '⛨',
  belt: '◐', sash: '∼', shoulder: '⌬',
};

const ASPECT_NAMES_DE: Record<AspectId, string> = {
  bulwark: 'Bollwerk',
  pyromant: 'Pyromant',
  stormcaller: 'Sturmrufer',
  vampire: 'Vampir',
  phantom: 'Phantom',
  engineer: 'Ingenieur',
};

const SLOTS: EquipmentSlot[] = [
  'weapon', 'helmet', 'shoulder', 'armor', 'sash', 'belt', 'accessory', 'shield',
];
const RARITIES: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];

/** Gear score per single instance — mirrors calcEquippedItemPower logic. */
function instancePower(inst: EquipmentInstance): number {
  const def = getItem(inst.itemId);
  if (!def?.stats) return 0;
  const base = inst.rolledStats ?? def.stats;
  const s = upgradedStats(base, inst.upgradeLevel, def.stats);
  const asc = inst.ascended ? 1.5 : 1;
  let atk = Math.round(s.attack * asc), defStat = Math.round(s.defense * asc), hp = Math.round(s.health * asc);
  if (inst.bonusStats) {
    for (const b of inst.bonusStats) {
      if (b.isMultiplier) continue;
      if (b.stat === 'attack') atk += b.value;
      else if (b.stat === 'defense') defStat += b.value;
      else if (b.stat === 'health') hp += b.value;
    }
  }
  const effectiveRarity = inst.ascended ? 4 : rarityRank(def.rarity);
  return atk + defStat + Math.floor(hp / 3) + effectiveRarity * 4 + inst.upgradeLevel;
}

/** Gear score: summed stats + rarity + upgrade-level weighting across equipped pieces. */
function calcItemPower(equipment: Record<string, EquipmentInstance | undefined>): number {
  return Object.values(equipment).reduce((sum, inst) => sum + (inst ? instancePower(inst) : 0), 0);
}

const RARITY_RANK: Record<Rarity, number> = {
  common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, mythic: 5,
};

function getMaxEquippedRarity(equipment: EquippedMap): Rarity | null {
  const rarities = (Object.values(equipment) as (EquipmentInstance | undefined)[])
    .filter(Boolean)
    .map((inst) => getItem(inst!.itemId)?.rarity)
    .filter(Boolean) as Rarity[];
  if (rarities.length === 0) return null;
  return rarities.reduce((max, r) => RARITY_RANK[r] > RARITY_RANK[max] ? r : max);
}

/** Compute the upgraded ATK/DEF/HP for an instance — used to show inline stats on equipment rows. */
function statsFor(inst: EquipmentInstance, def: { stats?: { attack: number; defense: number; health: number } }): { atk: number; def: number; hp: number } {
  if (!def.stats) return { atk: 0, def: 0, hp: 0 };
  const base = inst.rolledStats ?? def.stats;
  const s = upgradedStats(base, inst.upgradeLevel, def.stats);
  let atk = s.attack, defStat = s.defense, hp = s.health;
  if (inst.bonusStats) {
    for (const b of inst.bonusStats) {
      if (b.isMultiplier) continue;
      if (b.stat === 'attack') atk += b.value;
      else if (b.stat === 'defense') defStat += b.value;
      else if (b.stat === 'health') hp += b.value;
    }
  }
  return { atk, def: defStat, hp };
}

/** Single stat tile (used in the hero panel). Big number, small label, optional bonus. */
function StatTile({
  icon, label, value, bonus, accent = false,
}: { icon: string; label: string; value: number; bonus?: number; accent?: boolean }) {
  return (
    <div className={`stat-tile stat-tile--compact ${accent ? 'stat-tile--accent' : ''}`}>
      <span className="stat-tile__icon">{icon}</span>
      <span className="stat-tile__val">{value.toLocaleString('de-DE')}</span>
      <span className="stat-tile__lbl">{label}</span>
      {bonus !== undefined && bonus > 0 && (
        <span className="stat-tile__bonus">+{bonus}</span>
      )}
    </div>
  );
}

/** A single line in the Build-Stats grid. Renders nothing when value is 0
 *  so the panel only shows meaningful info for the current loadout. */
function BuildStatRow({
  icon, label, value, color, suffix = '%',
}: { icon: string; label: string; value: number; color?: string; suffix?: string }) {
  if (value <= 0) return null;
  return (
    <div className="build-stat">
      <span className="build-stat__icon" style={color ? { color } : undefined}>{icon}</span>
      <span className="build-stat__label">{label}</span>
      <span className="build-stat__value" style={color ? { color } : undefined}>
        {suffix === '%' ? Math.round(value) : value}{suffix}
      </span>
    </div>
  );
}


export function CharacterScreen(_props: CharacterScreenProps) {
  const player = useGameStore((s) => s.player);
  const salvageStashByRarity = useGameStore((s) => s.salvageStashByRarity);
  const myListings = useMarketStore((s) => s.myListings);
  const t = useT();

  const [detailInst, setDetailInst] = useState<EquipmentInstance | null>(null);
  const [upgradeInst, setUpgradeInst] = useState<EquipmentInstance | null>(null);
  const [invTab, setInvTab] = useState<'equipment' | 'resources'>('equipment');
  const [slotEquipTab, setSlotEquipTab] = useState<EquipmentSlot>('weapon');
  const [rarityFilter, setRarityFilter] = useState<Rarity | null>(null);
  const [setFilter, setSetFilter] = useState<'all' | ItemSetId | null>(null);
  const [setDropdownPos, setSetDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const setTriggerRef = useRef<HTMLButtonElement>(null);
  const setMenuRef = useRef<HTMLDivElement>(null);
  const [sortByPower, setSortByPower] = useState(false);
  // POC toggle: switch the Hero-Panel portrait between the live 3D character
  // and a single pixel-art sprite (PixelLab-generated). Lets us A/B the looks
  // without ripping out the 3D code yet.
  const [portraitMode, setPortraitMode] = useState<'3d' | 'pixel'>('3d');

  // Close set-dropdown on outside click. Trigger + menu are siblings in the DOM (menu is portalled),
  // so check both refs.
  useEffect(() => {
    if (!setDropdownPos) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (setTriggerRef.current?.contains(target)) return;
      if (setMenuRef.current?.contains(target)) return;
      setSetDropdownPos(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [setDropdownPos]);

  if (!player) return null;

  const maxRarity = getMaxEquippedRarity(player.equipment);
  const portraitGlow = maxRarity ? RARITY_COLOR[maxRarity] : undefined;

  // Items in market should not appear in the local stash (they're locked
  // server-side and would otherwise be salvageable / upgradeable in conflict).
  const listedIds = new Set(
    myListings.filter((l) => l.status === 'open').map((l) => l.instanceId),
  );
  const visibleStash = player.equipmentStash.filter((inst) => !listedIds.has(inst.id));

  const equipStats = getEquippedStats(player.equipment);
  const combatStats = calcPlayerStats(player, equipStats, player.boosts);
  // Stats the player would have at this level WITHOUT any equipment / sets / research / boosts.
  // Difference = total bonus from gear+skills+sets+research+boosts → shown as "+X" subtext.
  const baseStats = calcPlayerStats(
    { ...player, equipment: {}, boosts: [], research: {} },
    { attack: 0, defense: 0, health: 0 },
    [],
  );
  const statBonus = {
    attack: combatStats.attack - baseStats.attack,
    defense: combatStats.defense - baseStats.defense,
    maxHp: combatStats.maxHp - baseStats.maxHp,
  };
  const itemPower = calcItemPower(player.equipment);
  const nextLevelXp = xpRequiredForLevel(player.level + 1);
  const xpPct = nextLevelXp > 0 ? Math.min(100, (player.xp / nextLevelXp) * 100) : 100;

  // ── Inventory data ───────────────────────────────────────────

  // For bulk salvage counts per rarity (unfiltered)
  const stashByRarity: Record<Rarity, number> = { common: 0, uncommon: 0, rare: 0, epic: 0, legendary: 0, mythic: 0 };
  for (const inst of visibleStash) {
    const def = getItem(inst.itemId);
    if (def) stashByRarity[def.rarity] += 1;
  }

  const handleBulkSalvage = (rarity: Rarity | 'all') => {
    const count = rarity === 'all' ? visibleStash.length : stashByRarity[rarity];
    if (count === 0) return;
    const label = rarity === 'all' ? `alle ${count} Items` : `${count}× ${t.inventory.rarity[rarity]}`;
    if (confirm(`${label} zerlegen? Du erhältst Credits + Scrap zurück.`)) {
      salvageStashByRarity(rarity);
    }
  };

  return (
    <div className="hero-screen hero-screen--v2">
      {/* ── HERO HEADER: Name · Level · Power · XP ───────────────
          Single horizontal strip, name+level on the left, prominent
          POWER number on the right, XP bar full-width below. */}
      <header className="hero-header">
        <div className="hero-header__name">
          <span className="hero-header__name-text">{player.name}</span>
          <span className="hero-header__level">Lv {player.level}</span>
        </div>
        <div className="hero-header__power">
          <span className="hero-header__power-val">{itemPower}</span>
          <span className="hero-header__power-lbl">Power</span>
        </div>
      </header>
      <div className="hero-xp-strip">
        <div className="hero-xp-strip__bar">
          <div className="hero-xp-strip__fill" style={{ width: `${xpPct}%` }} />
        </div>
        <div className="hero-xp-strip__nums">
          {player.xp.toLocaleString('de-DE')} / {nextLevelXp.toLocaleString('de-DE')} XP
        </div>
      </div>

      {/* Daily quest card — sits between XP-strip and 3D portrait so the
          player sees today's objective immediately on hero-tab open. */}
      <DailyQuestPanel />

      {/* ── HERO PANEL: 3D portrait + 4 stat tiles ─────────────── */}
      <section className="hero-panel">
        <div className="hero-panel__portrait" style={{ '--glow': portraitGlow ?? 'transparent' } as React.CSSProperties}>
          {portraitMode === '3d' ? (
            <CharacterPortrait3D
              equipment={player.equipment}
              glowColor={portraitGlow}
              width={180}
              height={240}
            />
          ) : (
            <CharacterPortraitPixel
              frames={[
                '/assets/sprites/characters/titans_shell/idle_south_0.png',
                '/assets/sprites/characters/titans_shell/idle_south_1.png',
                '/assets/sprites/characters/titans_shell/idle_south_2.png',
                '/assets/sprites/characters/titans_shell/idle_south_3.png',
              ]}
              glowColor={portraitGlow}
              width={180}
              height={240}
            />
          )}
          <button
            type="button"
            className="hero-panel__portrait-toggle"
            onClick={() => setPortraitMode((m) => m === '3d' ? 'pixel' : '3d')}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              padding: '3px 8px',
              fontSize: 11,
              background: 'rgba(0,0,0,0.55)',
              color: '#cce8ff',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {portraitMode === '3d' ? '🖼 Pixel' : '🎮 3D'}
          </button>
          {portraitMode === '3d' && (
            <span className="hero-panel__rotate-hint">↻ ziehen zum drehen</span>
          )}
        </div>
        <div className="hero-panel__stats hero-panel__stats--compact">
          <StatTile icon="⚔" label="ATK" value={combatStats.attack} bonus={statBonus.attack} />
          <StatTile icon="🛡" label="DEF" value={combatStats.defense} bonus={statBonus.defense} />
          <StatTile icon="♥" label="HP"  value={combatStats.maxHp}   bonus={statBonus.maxHp} />
          <StatTile icon="✦" label="POWER" value={itemPower} accent />
        </div>
        {(() => {
          // ── Build-Stats panel — derived percentages from augments + aspects
          // so the player sees how their current loadout actually shapes their
          // combat profile beyond just ATK/DEF/HP/POWER.
          const lifestealPct =
            totalAspectValue(player, 'lifesteal', false, getItem) * 100;
          const specialDmgPct =
            totalAugmentValue(player.augments, 'special_dmg_pct') * 100;
          const lootBonusPct =
            totalAugmentValue(player.augments, 'loot_chance_pct') * 100;
          const xpBonusPct = totalAugmentValue(player.augments, 'xp_pct') * 100;
          const specialChargePct =
            totalAugmentValue(player.augments, 'special_charge_pct') * 100;
          const skillCdPct =
            totalAugmentValue(player.augments, 'skill_cd_pct') * 100;
          const onKillHealPct =
            totalAspectValue(player, 'on_kill', false, getItem) * 100;

          const affinityCounts = countAffinityPieces(player.equipment, getItem);
          const slotted: AspectId[] = [];
          if (player.aspects?.primary) slotted.push(player.aspects.primary);
          if (player.aspects?.secondary) slotted.push(player.aspects.secondary);

          const hasAnyStat =
            combatStats.critChance > 0 ||
            lifestealPct > 0 ||
            specialDmgPct > 0 ||
            lootBonusPct > 0 ||
            xpBonusPct > 0 ||
            specialChargePct > 0 ||
            skillCdPct > 0 ||
            onKillHealPct > 0 ||
            slotted.length > 0;
          if (!hasAnyStat) return null;

          return (
            <div className="hero-build-stats">
              <div className="hero-build-stats__title">Build-Profil</div>
              <div className="hero-build-stats__grid">
                {combatStats.critChance > 0 && (
                  <BuildStatRow
                    icon="💥"
                    label="Crit-Chance"
                    value={combatStats.critChance * 100}
                    color="#ff8a4a"
                  />
                )}
                {combatStats.critChance > 0 && (
                  <BuildStatRow
                    icon="✦"
                    label="Crit-Damage"
                    value={combatStats.critMult * 100}
                    color="#ff8a4a"
                  />
                )}
                <BuildStatRow icon="🩸" label="Lifesteal" value={lifestealPct} color="#ff5a6a" />
                <BuildStatRow icon="💀" label="Heal/Kill" value={onKillHealPct} color="#4affb0" />
                <BuildStatRow icon="⚡" label="Special-DMG" value={specialDmgPct} color="#c64aff" />
                <BuildStatRow icon="⏱" label="Special-Lade" value={specialChargePct} color="#c64aff" />
                <BuildStatRow icon="🌀" label="Skill-CD" value={skillCdPct} color="#36e8ff" />
                <BuildStatRow icon="🎁" label="Loot-Chance" value={lootBonusPct} color="#ffd84a" />
                <BuildStatRow icon="✨" label="XP-Gewinn" value={xpBonusPct} color="#4affb0" />
              </div>
              {(() => {
                const aspectsToShow = new Set<AspectId>(slotted);
                for (const [aspectId, count] of Object.entries(affinityCounts) as Array<[AspectId, number]>) {
                  if ((count ?? 0) > 0) aspectsToShow.add(aspectId);
                }
                if (aspectsToShow.size === 0) return null;

                // Affinity-tier thresholds. nextTier(N) returns "what's the next breakpoint" so we
                // can show "→ 6 für ×1.75". Mirrors the rules in affinityMultiplier().
                const TIERS: Array<{ at: number; mult: number }> = [
                  { at: 3, mult: 1.5 },
                  { at: 6, mult: 1.75 },
                  { at: 8, mult: 2.0 },
                ];
                const nextTier = (n: number) => TIERS.find((t) => n < t.at) ?? null;

                return (
                  <>
                    <div className="hero-build-stats__affinity-help">
                      Set-Stücke boosten die Passives des entsprechenden <strong>geslotteten</strong> Aspekts:
                      <br />3+ Stücke = ×1.5 · 6+ = ×1.75 · 8 = ×2.0. Aspekt slotten im Panel weiter unten.
                    </div>
                    <div className="hero-build-stats__affinity">
                      {[...aspectsToShow].map((aspectId) => {
                        const count = affinityCounts[aspectId] ?? 0;
                        const mult = affinityMultiplier(count);
                        const next = nextTier(count);
                        const def = ASPECTS[aspectId];
                        const isSlotted = slotted.includes(aspectId);
                        // Build a human-readable status line:
                        //   slotted + 0    : "geslottet · keine Set-Stücke"
                        //   slotted + tier : "geslottet · ×1.5 aktiv"
                        //   not slotted    : "Slotte für ×1.5 Boost"
                        let statusLine: string;
                        if (isSlotted) {
                          statusLine = count === 0
                            ? 'geslottet · ohne Set-Boost'
                            : `geslottet · ×${mult.toFixed(2)} aktiv`;
                        } else {
                          statusLine = mult > 1.0
                            ? `nicht geslottet · ${count} St. = ×${mult.toFixed(2)} möglich`
                            : `nicht geslottet · ab 3 St. greift Boost`;
                        }
                        return (
                          <div
                            key={aspectId}
                            className={`hero-aff-pill hero-aff-pill--detailed ${isSlotted ? 'hero-aff-pill--slotted' : 'hero-aff-pill--dim'}`}
                            style={{ '--aff-color': def.themeColor } as React.CSSProperties}
                            title={isSlotted
                              ? `${ASPECT_NAMES_DE[aspectId]} ist als Aspekt geslottet. Passives sind aktiv. Bei ${count} matchenden Equipment-Stücken wirken sie mit ×${mult.toFixed(2)}.`
                              : `Du trägst ${count} ${ASPECT_NAMES_DE[aspectId]}-Item${count === 1 ? '' : 's'}, aber der Aspekt ist NICHT als Primary/Secondary geslottet — die Passives wirken gar nicht. Slotte unten im Aspekt-Panel.`}
                          >
                            <div className="hero-aff-pill__top">
                              <span className="hero-aff-pill__name">{ASPECT_NAMES_DE[aspectId]}</span>
                              <span className="hero-aff-pill__count">{count}/8</span>
                            </div>
                            <div className="hero-aff-pill__status">{statusLine}</div>
                            {next && (
                              <div className="hero-aff-pill__next">
                                → noch {next.at - count} für ×{next.mult.toFixed(2)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          );
        })()}
      </section>

      {/* ── PROGRESSION CURRENCIES: Ascension Fragments + Soft-Pity ─────
          Both are earned across runs and visible from the Hero-Tab so the
          player always sees their next-step progress at a glance. */}
      <section style={{
        display: 'flex', gap: '0.75rem', alignItems: 'stretch',
        padding: '0.5rem 0', flexWrap: 'wrap',
      }}>
        <div style={{
          flex: 1, minWidth: '8rem', padding: '0.5rem 0.75rem',
          background: 'rgba(255, 154, 43, 0.08)',
          border: '1px solid rgba(255, 154, 43, 0.35)', borderRadius: '0.5rem',
          display: 'flex', flexDirection: 'column', gap: '0.15rem',
        }}>
          <span style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: '#ff9a2b', textTransform: 'uppercase' }}>
            ✦ Fragments
          </span>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#ffd84a' }}>
            {(player.ascensionFragments ?? 0).toLocaleString('de-DE')}
          </span>
          <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.55)' }}>
            L10-Item zerlegen → +1 · 3 Fragments für Ascension
          </span>
        </div>
        <div style={{
          flex: 1, minWidth: '8rem', padding: '0.5rem 0.75rem',
          background: 'rgba(54, 232, 255, 0.08)',
          border: '1px solid rgba(54, 232, 255, 0.35)', borderRadius: '0.5rem',
          display: 'flex', flexDirection: 'column', gap: '0.15rem',
        }}>
          <span style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: '#36e8ff', textTransform: 'uppercase' }}>
            🍀 Glück
          </span>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#9affc8' }}>
            +{Math.round((player.legendaryPity ?? 0) * 100)}%
          </span>
          <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.55)' }}>
            Soft-Pity für Legendaries · max +30 % bei langen Pechsträhnen
          </span>
        </div>
      </section>

      {/* ── EQUIPMENT: full-width row list (one per slot) ─────────
          Each row shows icon, slot label, item name, upgrade level,
          rarity tag and a summary of the item's primary stat. Tap a
          row to open detail and focus the inventory on that slot. */}
      <section className="hero-equip">
        <h3 className="hero-section-title">Ausrüstung</h3>
        <div className="equip-list">
          {SLOTS.map((slot) => {
            const inst = player.equipment[slot];
            const def = inst ? getItem(inst.itemId) : null;
            const color = def ? RARITY_COLOR[def.rarity] : 'rgba(255,255,255,0.16)';
            const stats = inst && def ? statsFor(inst, def) : null;
            const handleClick = () => {
              setSlotEquipTab(slot);
              if (inst) setDetailInst(inst);
              else {
                // jump to inventory tab + scroll to inventory section
                setInvTab('equipment');
              }
            };
            return (
              <button
                key={slot}
                className={`equip-row ${inst ? 'equip-row--filled' : 'equip-row--empty'}`}
                style={{ '--rarity': color } as React.CSSProperties}
                onClick={handleClick}
                title={inst && def ? `${itemName(def.nameKey)} +${inst.upgradeLevel}` : t.character.slots[slot]}
              >
                <span className="equip-row__icon-wrap">
                  {inst && def ? (
                    <ItemIcon itemId={def.id} sizeRem={2.6} />
                  ) : (
                    <span className="equip-row__glyph">{SLOT_GLYPH[slot]}</span>
                  )}
                </span>
                <span className="equip-row__main">
                  <span className="equip-row__slot">{t.character.slots[slot]}</span>
                  {inst && def ? (
                    <span className="equip-row__name">
                      {itemName(def.nameKey)}
                      <span className="equip-row__lvl">+{inst.upgradeLevel}</span>
                    </span>
                  ) : (
                    <span className="equip-row__empty">— leer · tippen zum ausrüsten —</span>
                  )}
                  {stats && (
                    <span className="equip-row__stats">
                      {stats.atk > 0 && <span className="equip-row__stat">⚔ {stats.atk}</span>}
                      {stats.def > 0 && <span className="equip-row__stat">🛡 {stats.def}</span>}
                      {stats.hp > 0 && <span className="equip-row__stat">♥ {stats.hp}</span>}
                    </span>
                  )}
                </span>
                {inst && def && (
                  <span className="equip-row__rarity" style={{ color }}>
                    {rarityLabel(def.rarity)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* ── SKILLS + SETS: collapsible (cleaner default view) ── */}
      <details className="hero-collapsible">
        <summary className="hero-collapsible__summary">
          <span>Skills &amp; Set-Bonusse</span>
          <span className="hero-collapsible__chev">▾</span>
        </summary>
        <div className="hero-collapsible__body">
          <SkillSlots player={player} />
          <SetBonusBar equipment={player.equipment} />
        </div>
      </details>

      {/* ── INVENTORY SECTION: Tabbed (Equipment / Resources) ──── */}
      <section className="hero-inventory">
        <div className="hero-inventory__tabs">
          <button
            className={`hero-inv-tab ${invTab === 'equipment' ? 'hero-inv-tab--active' : ''}`}
            onClick={() => setInvTab('equipment')}
          >
            Ausrüstung ({visibleStash.length})
          </button>
          <button
            className={`hero-inv-tab ${invTab === 'resources' ? 'hero-inv-tab--active' : ''}`}
            onClick={() => setInvTab('resources')}
          >
            Ressourcen ({player.inventory.slots.length}/{player.inventory.maxSlots})
          </button>
        </div>

        {invTab === 'equipment' && (
          <>
            {/* ── Filters + Sort (apply across all slots, persist on slot switch) ── */}
            {(() => {
              // Chip visibility computed from EVERY equipment item the player owns,
              // not just the active slot — filters keep their state across slot tabs.
              const allItems = [
                ...(Object.values(player.equipment).filter(Boolean) as EquipmentInstance[]),
                ...visibleStash,
              ];
              const rarities = [...new Set(
                allItems.map((inst) => getItem(inst.itemId)?.rarity).filter(Boolean)
              )] as Rarity[];
              const hasSetItems = allItems.some((inst) => !!getItem(inst.itemId)?.setId);
              if (allItems.length === 0) return null;
              // Counts on each chip reflect the CURRENT slot only (so the user sees
              // how many results the filter will produce in the visible grid).
              const slotItemsForCount = [
                ...(player.equipment[slotEquipTab] ? [player.equipment[slotEquipTab]!] : []),
                ...visibleStash.filter((inst) => getItem(inst.itemId)?.equipSlot === slotEquipTab),
              ];
              return (
                <div className="hero-filters hero-filters--scroll">
                  <button
                    className={`hero-filter ${!rarityFilter ? 'hero-filter--active' : ''}`}
                    onClick={() => setRarityFilter(null)}
                  >
                    Alle ({slotItemsForCount.length})
                  </button>
                  {RARITIES.filter((r) => rarities.includes(r)).map((r) => {
                    const c = slotItemsForCount.filter((inst) => getItem(inst.itemId)?.rarity === r).length;
                    return (
                      <button
                        key={r}
                        className={`hero-filter ${rarityFilter === r ? 'hero-filter--active' : ''}`}
                        style={{ '--filter-color': RARITY_COLOR[r] } as React.CSSProperties}
                        onClick={() => setRarityFilter((cur) => cur === r ? null : r)}
                      >
                        {t.inventory.rarity[r]} ({c})
                      </button>
                    );
                  })}
                  {hasSetItems && (() => {
                    // Sets the player has at least one piece of (across equipped + stash).
                    const setsOwned = new Map<ItemSetId, Set<EquipmentSlot>>();
                    for (const inst of allItems) {
                      const def = getItem(inst.itemId);
                      if (!def?.setId || !def.equipSlot) continue;
                      if (!setsOwned.has(def.setId)) setsOwned.set(def.setId, new Set());
                      setsOwned.get(def.setId)!.add(def.equipSlot);
                    }
                    const setIds = Array.from(setsOwned.keys());
                    const label = setFilter === 'all'
                      ? 'Alle Sets'
                      : setFilter
                      ? SET_NAMES[setFilter]
                      : 'Sets';
                    const open = !!setDropdownPos;
                    const toggleOpen = () => {
                      if (open) {
                        setSetDropdownPos(null);
                      } else if (setTriggerRef.current) {
                        const r = setTriggerRef.current.getBoundingClientRect();
                        setSetDropdownPos({ top: r.bottom + 4, left: r.left });
                      }
                    };
                    const pickAndClose = (next: 'all' | ItemSetId | null) => {
                      setSetFilter(next);
                      setSetDropdownPos(null);
                    };
                    return (
                      <>
                        <button
                          ref={setTriggerRef}
                          className={`hero-filter ${setFilter ? 'hero-filter--active' : ''}`}
                          style={{ '--filter-color': '#ffd700' } as React.CSSProperties}
                          onClick={toggleOpen}
                        >
                          ◈ {label} ▾
                        </button>
                        {open && setDropdownPos && createPortal(
                          <div
                            ref={setMenuRef}
                            className="hero-filter-dropdown__menu"
                            style={{ position: 'fixed', top: setDropdownPos.top, left: setDropdownPos.left }}
                          >
                            <button
                              className={`hero-filter-dropdown__item ${setFilter === null ? 'hero-filter-dropdown__item--active' : ''}`}
                              onClick={() => pickAndClose(null)}
                            >
                              — kein Filter —
                            </button>
                            <button
                              className={`hero-filter-dropdown__item ${setFilter === 'all' ? 'hero-filter-dropdown__item--active' : ''}`}
                              onClick={() => pickAndClose('all')}
                            >
                              ◈ Alle Sets
                            </button>
                            {setIds.map((sid) => {
                              const piecesOwned = setsOwned.get(sid)!.size;
                              return (
                                <button
                                  key={sid}
                                  className={`hero-filter-dropdown__item ${setFilter === sid ? 'hero-filter-dropdown__item--active' : ''}`}
                                  onClick={() => pickAndClose(sid)}
                                >
                                  <span>{SET_NAMES[sid]}</span>
                                  <span className="hero-filter-dropdown__count">{piecesOwned}/4</span>
                                </button>
                              );
                            })}
                          </div>,
                          document.body,
                        )}
                      </>
                    );
                  })()}
                  {allItems.length > 1 && (
                    <button
                      className={`hero-filter ${sortByPower ? 'hero-filter--active' : ''}`}
                      style={{ '--filter-color': '#4aa7ff' } as React.CSSProperties}
                      onClick={() => setSortByPower((cur) => !cur)}
                    >
                      ✦ Power
                    </button>
                  )}
                </div>
              );
            })()}

            {/* ── Slot-Tabs (filter persistence across switches) ────────────────── */}
            <div className="equip-slot-tabs">
              {SLOTS.map((slot) => {
                const stashCount = visibleStash.filter(
                  (inst) => getItem(inst.itemId)?.equipSlot === slot
                ).length;
                const hasEquipped = !!player.equipment[slot];
                return (
                  <button
                    key={slot}
                    className={`equip-slot-tab ${slotEquipTab === slot ? 'equip-slot-tab--active' : ''}`}
                    onClick={() => setSlotEquipTab(slot)}
                  >
                    <span className="equip-slot-tab__glyph">{SLOT_GLYPH[slot]}</span>
                    <span className="equip-slot-tab__label">{t.character.slots[slot]}</span>
                    {hasEquipped && <span className="equip-slot-tab__dot" />}
                    {stashCount > 0 && <span className="equip-slot-tab__count">{stashCount}</span>}
                  </button>
                );
              })}
            </div>

            {/* ── Item grid — click always opens detail modal ───── */}
            {(() => {
              const slotEquipped = player.equipment[slotEquipTab] ?? null;
              const slotStash = visibleStash.filter(
                (inst) => getItem(inst.itemId)?.equipSlot === slotEquipTab
              );
              let slotItems = [...(slotEquipped ? [slotEquipped] : []), ...slotStash];
              if (rarityFilter) slotItems = slotItems.filter((inst) => getItem(inst.itemId)?.rarity === rarityFilter);
              if (setFilter === 'all') {
                slotItems = slotItems.filter((inst) => !!getItem(inst.itemId)?.setId);
              } else if (setFilter) {
                slotItems = slotItems.filter((inst) => getItem(inst.itemId)?.setId === setFilter);
              }
              if (sortByPower) slotItems = [...slotItems].sort((a, b) => instancePower(b) - instancePower(a));

              if (slotItems.length === 0) {
                return (
                  <div className="hero-inv-empty">
                    {slotEquipped || slotStash.length > 0
                      ? 'Kein Item dieser Seltenheit für diesen Slot.'
                      : player.equipmentStash.length === 0
                        ? 'Stash leer — geh in einen Dungeon.'
                        : `Keine ${t.character.slots[slotEquipTab]}-Items im Stash.`}
                  </div>
                );
              }

              return (
                <div className="hero-inv-grid">
                  {slotItems.map((inst) => {
                    const def = getItem(inst.itemId);
                    if (!def) return null;
                    const color = RARITY_COLOR[def.rarity];
                    const isEquipped = slotEquipped?.id === inst.id;
                    const isListed = listedIds.has(inst.id);
                    return (
                      <button
                        key={inst.id}
                        className={[
                          'hero-inv-cell',
                          `hero-inv-cell--${def.rarity}`,
                          isEquipped ? 'hero-inv-cell--equipped' : '',
                          def.setId ? 'hero-inv-cell--set' : '',
                          inst.ascended ? 'hero-inv-cell--ascended' : '',
                          isListed ? 'hero-inv-cell--listed' : '',
                        ].filter(Boolean).join(' ')}
                        style={{ '--item-color': inst.ascended ? '#ffd700' : color } as React.CSSProperties}
                        onClick={() => setDetailInst(inst)}
                        title={isEquipped ? `${itemName(def.nameKey)} — Angelegt` : itemName(def.nameKey)}
                      >
                        {isEquipped && <span className="hero-inv-cell__equipped-badge">✓</span>}
                        {isListed && <span className="hero-inv-cell__listed-badge">🏷</span>}
                        <ItemIcon itemId={def.id} sizeRem={2} />
                        <span className="hero-inv-cell__level">
                          +{inst.upgradeLevel}
                          {inst.bonusStats && inst.bonusStats.length > 0 && (
                            <span className="hero-inv-cell__bonus-dot">✦</span>
                          )}
                        </span>
                        <span className="hero-inv-cell__name">{itemName(def.nameKey)}</span>
                        {inst.ascended ? (
                          <span className="hero-inv-cell__ascended">✦ Legendär</span>
                        ) : def.setId ? (
                          <span className="hero-inv-cell__set-badge">◈ {SET_NAMES[def.setId].split('-')[0]}</span>
                        ) : (
                          <span className="hero-inv-cell__rarity" style={{ color }}>{rarityLabel(def.rarity)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })()}

            {/* Bulk salvage */}
            {visibleStash.length > 0 && (
              <div className="hero-bulk">
                <span className="hero-bulk__label">Zerlegen:</span>
                {RARITIES.map((r) => {
                  const c = stashByRarity[r];
                  if (c === 0) return null;
                  return (
                    <button
                      key={r}
                      className="hero-bulk__btn"
                      style={{
                        borderColor: `color-mix(in srgb, ${RARITY_COLOR[r]} 50%, transparent)`,
                        color: RARITY_COLOR[r],
                      }}
                      onClick={() => handleBulkSalvage(r)}
                    >
                      🔧 {t.inventory.rarity[r]} ×{c}
                    </button>
                  );
                })}
                <button
                  className="hero-bulk__btn hero-bulk__btn--all"
                  onClick={() => handleBulkSalvage('all')}
                >
                  🔧 ALLE ×{visibleStash.length}
                </button>
              </div>
            )}
          </>
        )}

        {invTab === 'resources' && (
          <>
            {player.inventory.slots.length === 0 ? (
              <div className="hero-inv-empty">{t.inventory.empty}</div>
            ) : (
              <div className="hero-inv-grid">
                {player.inventory.slots.map((stack, i) => {
                  const def = getItem(stack.itemId);
                  if (!def) return null;
                  const color = RARITY_COLOR[def.rarity];
                  return (
                    <div
                      key={`${stack.itemId}-${i}`}
                      className={`hero-inv-cell hero-inv-cell--${def.rarity} hero-inv-cell--readonly`}
                      style={{ '--item-color': color } as React.CSSProperties}
                    >
                      <ItemIcon itemId={def.id} sizeRem={2} />
                      <span className="hero-inv-cell__qty">×{stack.quantity.toLocaleString('de-DE')}</span>
                      <span className="hero-inv-cell__name">{itemName(def.nameKey)}</span>
                      <span className="hero-inv-cell__rarity" style={{ color }}>
                        {rarityLabel(def.rarity)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>

      {/* P2.B Aspekt-System — build-defining identity layer over augments.
          Sits below the inventory + above achievements so the player sees
          equipment first, then identity, then long-tail milestones. */}
      <AspectPanel />

      {/* Achievements panel — collapsible. Sits at the bottom of the hero
          tab so it doesn't push the inventory off-screen on first scroll. */}
      <AchievementsPanel />

      {/* Modals */}
      {detailInst && (
        <ItemDetailModal
          instance={detailInst}
          onClose={() => setDetailInst(null)}
          onUpgrade={(inst) => setUpgradeInst(inst)}
        />
      )}
      {upgradeInst && (
        <UpgradeModal
          instance={upgradeInst}
          onClose={() => setUpgradeInst(null)}
        />
      )}
    </div>
  );
}
